import React from 'react'
import PropTypes from 'prop-types'
import SubjectToCSSName from '../../less/SubjectToCSSName'
import './box.css'


/*
  Adapted from Pinterest's Gestalt library.
  https://github.com/pinterest/gestalt/tree/master/packages/gestalt/src/Box
*/


/*
# Helpers.
*/
export const identity = () => ({
  className: new Set(),
  inlineStyle: {},
})

export const fromClassName = (...classNames: Array<string>) => ({
  className: new Set(classNames),
  inlineStyle: {},
})

export const fromInlineStyle = (inlineStyle: {
  [key: string]: any,
}) => ({
  className: new Set(),
  inlineStyle,
})

export const concat = (styles) =>
  styles.reduce(
    (
      { className: classNameA, inlineStyle: inlineStyleA },
      { className: classNameB, inlineStyle: inlineStyleB }
    ) => ({
      className: new Set([...classNameA, ...classNameB]),
      inlineStyle: { ...inlineStyleA, ...inlineStyleB },
    }),
    identity()
  )

export const mapClassName = (fn: (x: string) => string) => ({
  className,
  inlineStyle,
}) => ({
  className: new Set(Array.from(className).map(fn)),
  inlineStyle,
})

export const toProps = ({
  className,
  inlineStyle,
}): { className: string, style: { [key: string]: any } } => {
  const props = {}

  if (className.size > 0) {
    // Sorting here ensures that classNames are always stable, reducing diff
    // churn. Box usually has a small number of properties so it's not a perf
    // concern.
    props.className = Array.from(className)
      .sort()
      .join(' ')
  }

  if (Object.keys(inlineStyle).length > 0) {
    props.style = inlineStyle
  }

  return props
}

// Adds a classname when a property is present.
//
//     <Box top />
//
export const toggle = (...classNames) => val =>
  val ? fromClassName(...classNames) : identity()

// Maps string values to classes
//
//     <Box alignItems="center" />
//
export const mapping = map => val =>
  Object.prototype.hasOwnProperty.call(map, val)
    ? fromClassName(map[val])
    : identity()

// Maps a range of integers to a range of classnames
//
//     <Box padding={1} />
//
export const range = scale => n =>
  fromClassName(`${scale}${n < 0 ? `N${Math.abs(n)}` : n}`)

// Like `range`, maps a range of integers to a range of classnames, excluding
// zero values.
//
//     <Box padding={0} />
export const rangeWithoutZero = scale => n =>
  n === 0 ? identity() : range(scale)(n)

// Binds a string classname to the value in an object. Useful when interacting
// with ranges that need to come dynamically from a style object. This is
// similar to the NPM package 'classnames/bind'.
export const bind = (fn, scope) => val =>
  mapClassName(name => scope[name])(fn(val))

// This takes a series of the previously defined functors, runs them all
// against a value and returns the set of their classnames.
export const union = (...fns) => val => concat(fns.map(fn => fn(val)))

const columnsClasses = {}
for (let num = 1; num < 13; num++) {
  columnsClasses[`xsCol${num}`] = `xsCol${num}`
  columnsClasses[`smCol${num}`] = `smCol${num}`
  columnsClasses[`mdCol${num}`] = `mdCol${num}`
  columnsClasses[`lgCol${num}`] = `lgCol${num}`
}
const column = union(bind(rangeWithoutZero('xsCol'), columnsClasses), toggle('col'))
const smColumn = union(bind(rangeWithoutZero('smCol'), columnsClasses), toggle('col'))
const mdColumn = union(bind(rangeWithoutZero('mdCol'), columnsClasses), toggle('col'))
const lgColumn = union(bind(rangeWithoutZero('lgCol'), columnsClasses), toggle('col'))

const whitespace = {}
for (let num = 0; num < 13; num++) {
  whitespace[`marginTop${num}`] = `marginTop${num}`
  whitespace[`marginLeft${num}`] = `marginLeft${num}`
  whitespace[`marginRight${num}`] = `marginRight${num}`
  whitespace[`marginBottom${num}`] = `marginBottom${num}`
  whitespace[`marginTopN${num}`] = `marginTopN${num}`
  whitespace[`marginLeftN${num}`] = `marginLeftN${num}`
  whitespace[`marginRightN${num}`] = `marginRightN${num}`
  whitespace[`marginBottomN${num}`] = `marginBottomN${num}`
  whitespace[`paddingRight${num}`] = `paddingRight${num}`
  whitespace[`paddingLeft${num}`] = `paddingLeft${num}`
  whitespace[`paddingBottom${num}`] = `paddingBottom${num}`
  whitespace[`paddingTop${num}`] = `paddingTop${num}`
  whitespace[`paddingX${num}`] = `paddingX${num}`
  whitespace[`paddingY${num}`] = `paddingY${num}`

  whitespace[`smMarginTop${num}`] = `smMarginTop${num}`
  whitespace[`smMarginLeft${num}`] = `smMarginLeft${num}`
  whitespace[`smMarginRight${num}`] = `smMarginRight${num}`
  whitespace[`smMarginBottom${num}`] = `smMarginBottom${num}`
  whitespace[`smMarginTopN${num}`] = `smMarginTopN${num}`
  whitespace[`smMarginLeftN${num}`] = `smMarginLeftN${num}`
  whitespace[`smMarginRightN${num}`] = `smMarginRightN${num}`
  whitespace[`smMarginBottomN${num}`] = `smMarginBottomN${num}`
  whitespace[`smPaddingRight${num}`] = `smPaddingRight${num}`
  whitespace[`smPaddingLeft${num}`] = `smPaddingLeft${num}`
  whitespace[`smPaddingBottom${num}`] = `smPaddingBottom${num}`
  whitespace[`smPaddingTop${num}`] = `smPaddingTop${num}`
  whitespace[`smPaddingX${num}`] = `smPaddingX${num}`
  whitespace[`smPaddingY${num}`] = `smPaddingY${num}`

  whitespace[`mdMarginTop${num}`] = `mdMarginTop${num}`
  whitespace[`mdMarginLeft${num}`] = `mdMarginLeft${num}`
  whitespace[`mdMarginRight${num}`] = `mdMarginRight${num}`
  whitespace[`mdMarginBottom${num}`] = `mdMarginBottom${num}`
  whitespace[`mdMarginTopN${num}`] = `mdMarginTopN${num}`
  whitespace[`mdMarginLeftN${num}`] = `mdMarginLeftN${num}`
  whitespace[`mdMarginRightN${num}`] = `mdMarginRightN${num}`
  whitespace[`mdMarginBottomN${num}`] = `mdMarginBottomN${num}`
  whitespace[`mdPaddingRight${num}`] = `mdPaddingRight${num}`
  whitespace[`mdPaddingLeft${num}`] = `mdPaddingLeft${num}`
  whitespace[`mdPaddingBottom${num}`] = `mdPaddingBottom${num}`
  whitespace[`mdPaddingTop${num}`] = `mdPaddingTop${num}`
  whitespace[`mdPaddingX${num}`] = `mdPaddingX${num}`
  whitespace[`mdPaddingY${num}`] = `mdPaddingY${num}`

  whitespace[`lgMarginTop${num}`] = `lgMarginTop${num}`
  whitespace[`lgMarginLeft${num}`] = `lgMarginLeft${num}`
  whitespace[`lgMarginRight${num}`] = `lgMarginRight${num}`
  whitespace[`lgMarginBottom${num}`] = `lgMarginBottom${num}`
  whitespace[`lgMarginTopN${num}`] = `lgMarginTopN${num}`
  whitespace[`lgMarginLeftN${num}`] = `lgMarginLeftN${num}`
  whitespace[`lgMarginRightN${num}`] = `lgMarginRightN${num}`
  whitespace[`lgMarginBottomN${num}`] = `lgMarginBottomN${num}`
  whitespace[`lgPaddingRight${num}`] = `lgPaddingRight${num}`
  whitespace[`lgPaddingLeft${num}`] = `lgPaddingLeft${num}`
  whitespace[`lgPaddingBottom${num}`] = `lgPaddingBottom${num}`
  whitespace[`lgPaddingTop${num}`] = `lgPaddingTop${num}`
  whitespace[`lgPaddingX${num}`] = `lgPaddingX${num}`
  whitespace[`lgPaddingY${num}`] = `lgPaddingY${num}`
}

const marginTop = bind(range('marginTop'), whitespace)
const marginRight = bind(range('marginRight'), whitespace)
const marginBottom = bind(range('marginBottom'), whitespace)
const marginLeft = bind(range('marginLeft'), whitespace)
const margin = union(marginTop, marginBottom, marginLeft, marginRight)

const smMarginTop = bind(range('smMarginTop'), whitespace)
const smMarginRight = bind(range('smMarginRight'), whitespace)
const smMarginBottom = bind(range('smMarginBottom'), whitespace)
const smMarginLeft = bind(range('smMarginLeft'), whitespace)
const smMargin = union(
  smMarginTop,
  smMarginBottom,
  smMarginLeft,
  smMarginRight
)

const mdMarginTop = bind(range('mdMarginTop'), whitespace)
const mdMarginRight = bind(range('mdMarginRight'), whitespace)
const mdMarginBottom = bind(range('mdMarginBottom'), whitespace)
const mdMarginLeft = bind(range('mdMarginLeft'), whitespace)
const mdMargin = union(
  mdMarginTop,
  mdMarginBottom,
  mdMarginLeft,
  mdMarginRight
)

const lgMarginTop = bind(range('lgMarginTop'), whitespace)
const lgMarginRight = bind(range('lgMarginRight'), whitespace)
const lgMarginBottom = bind(range('lgMarginBottom'), whitespace)
const lgMarginLeft = bind(range('lgMarginLeft'), whitespace)
const lgMargin = union(
  lgMarginTop,
  lgMarginBottom,
  lgMarginLeft,
  lgMarginRight
)

const paddingTop = bind(range('paddingTop'), whitespace)
const paddingRight = bind(range('paddingRight'), whitespace)
const paddingBottom = bind(range('paddingBottom'), whitespace)
const paddingLeft = bind(range('paddingLeft'), whitespace)

const smPaddingTop = bind(range('smPaddingTop'), whitespace)
const smPaddingRight = bind(range('smPaddingRight'), whitespace)
const smPaddingBottom = bind(range('smPaddingBottom'), whitespace)
const smPaddingLeft = bind(range('smPaddingLeft'), whitespace)

const mdPaddingTop = bind(range('mdPaddingTop'), whitespace)
const mdPaddingRight = bind(range('mdPaddingRight'), whitespace)
const mdPaddingBottom = bind(range('mdPaddingBottom'), whitespace)
const mdPaddingLeft = bind(range('mdPaddingLeft'), whitespace)

const lgPaddingTop = bind(range('lgPaddingTop'), whitespace)
const lgPaddingRight = bind(range('lgPaddingRight'), whitespace)
const lgPaddingBottom = bind(range('lgPaddingBottom'), whitespace)
const lgPaddingLeft = bind(range('lgPaddingLeft'), whitespace)

const paddingX = bind(range('paddingX'), whitespace)
const paddingY = bind(range('paddingY'), whitespace)
const padding = union(paddingX, paddingY, paddingTop, paddingBottom, paddingLeft, paddingRight)

const smPaddingX = bind(range('smPaddingX'), whitespace)
const smPaddingY = bind(range('smPaddingY'), whitespace)
const smPadding = union(smPaddingX, smPaddingY, smPaddingTop, smPaddingBottom, smPaddingLeft, smPaddingRight)

const mdPaddingX = bind(range('mdPaddingX'), whitespace)
const mdPaddingY = bind(range('mdPaddingY'), whitespace)
const mdPadding = union(mdPaddingX, mdPaddingY, mdPaddingTop, mdPaddingBottom, mdPaddingRight, mdPaddingLeft)

const lgPaddingX = bind(range('lgPaddingX'), whitespace)
const lgPaddingY = bind(range('lgPaddingY'), whitespace)
const lgPadding = union(lgPaddingX, lgPaddingY, lgPaddingTop, lgPaddingBottom, lgPaddingRight, lgPaddingLeft)

const subjectColorMap = {}
const subjectBorderColorMap = {}
for (let subject of Object.values(SubjectToCSSName)) {
  subjectColorMap[`${subject}-search`] = `box--${subject}--search`
  subjectColorMap[`${subject}-nav`] = `box--${subject}--nav`
  subjectColorMap[`${subject}-cta`] = `box--${subject}--cta`
  subjectColorMap[`${subject}-subject`] = `box--${subject}--subject`

  subjectBorderColorMap[`${subject}-search`] = `box-border-color--${subject}--search`
  subjectBorderColorMap[`${subject}-nav`] = `box-border-color--${subject}--nav`
  subjectBorderColorMap[`${subject}-cta`] = `box-border-color--${subject}--cta`
  subjectBorderColorMap[`${subject}-subject`] = `box-border-color--${subject}--subject`
}
// Props to class/style mapping.
const propToFn = {
  column,
  smColumn,
  mdColumn,
  lgColumn,
  clickable: toggle('box--clickable'),
  display: mapping({
    none: 'xs-boxDisplay--none',
    flex: 'xs-boxDisplay--flex',
    block: 'xs-boxDisplay--block',
    inlineBlock: 'xs-boxDisplay--inlineBlock',
  }),
  direction: mapping({
    row: 'xs-boxDirection--row',
    column: 'xs-boxDirection--column',
  }),
  smDisplay: mapping({
    none: 'sm-boxDisplay--none',
    flex: 'sm-boxDisplay--flex',
    block: 'sm-boxDisplay--block',
    inlineBlock: 'sm-boxDisplay--inlineBlock',
  }),
  smDirection: mapping({
    row: 'sm-boxDirection--row',
    column: 'sm-boxDirection--column',
  }),
  mdDisplay: mapping({
    none: 'md-boxDisplay--none',
    flex: 'md-boxDisplay--flex',
    block: 'md-boxDisplay--block',
    inlineBlock: 'md-boxDisplay--inlineBlock',
  }),
  mdDirection: mapping({
    row: 'md-boxDirection--row',
    column: 'md-boxDirection--column',
  }),
  lgDisplay: mapping({
    none: 'lg-boxDisplay--none',
    flex: 'lg-boxDisplay--flex',
    block: 'lg-boxDisplay--block',
    inlineBlock: 'lg-boxDisplay--inlineBlock',
  }),
  lgDirection: mapping({
    row: 'lg-boxDirection--row',
    column: 'lg-boxDirection--column',
  }),
  alignItems: mapping({
    start: 'box-align-items-start',
    end: 'box-align-items-end',
    center: 'box-align-items-center',
    baseline: 'box-align-items-baseline',
    // default: stretch
  }),
  alignSelf: mapping({
    start: 'box-align-self-start',
    end: 'box-align-self-end',
    center: 'box-align-self-center',
    baseline: 'box-align-self-baseline',
    stretch: 'box-align-self-stretch',
    // default: auto
  }),
  bottom: 'box-bottom0',
  color: mapping({
    white: 'box--white',
    darkGray: 'box--darkGray',
    midGray: 'box--midGray',
    lightGray: 'box--lightGray',
    coolGray: 'box--coolGray',
    ...subjectColorMap
    // default: transparent
  }),
  flexShrink: flexShrink => fromInlineStyle({flexShrink}),
  flexGrow: flexGrow => fromInlineStyle({flexGrow}),
  height: height => fromInlineStyle({ height }),
  justifyContent: mapping({
    end: 'box-justify-end',
    center: 'box-justify-center',
    between: 'box-justify-between',
    around: 'box-justify-around',
    // default: start
  }),
  left: toggle('box-left0'),
  margin,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  smMargin,
  smMarginTop,
  smMarginRight,
  smMarginBottom,
  smMarginLeft,
  mdMargin,
  mdMarginTop,
  mdMarginRight,
  mdMarginBottom,
  mdMarginLeft,
  lgMargin,
  lgMarginTop,
  lgMarginRight,
  lgMarginBottom,
  lgMarginLeft,
  maxHeight: maxHeight => fromInlineStyle({ maxHeight }),
  maxWidth: maxWidth => fromInlineStyle({ maxWidth }),
  minHeight: minHeight => fromInlineStyle({ minHeight }),
  minWidth: minWidth => fromInlineStyle({ minWidth }),
  whiteSpace: mapping({nowrap: 'box-whitespace--nowrap'}),
  textOverflow: mapping({ellipsis: 'box-textoverflow--ellipsis'}),
  padding,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  paddingX,
  paddingY,
  smPadding,
  smPaddingTop,
  smPaddingRight,
  smPaddingBottom,
  smPaddingLeft,
  smPaddingX,
  smPaddingY,
  mdPadding,
  mdPaddingTop,
  mdPaddingRight,
  mdPaddingBottom,
  mdPaddingLeft,
  mdPaddingX,
  mdPaddingY,
  lgPadding,
  lgPaddingTop,
  lgPaddingRight,
  lgPaddingBottom,
  lgPaddingLeft,
  lgPaddingX,
  lgPaddingY,
  overflow: mapping({
    visible: 'box-overflow--visible',
    hidden: 'box-overflow--hidden',
    scrollX: 'box-overflow--scrollx',
    scrollY: 'box-overflow--scrolly',
    scroll: 'box-overflow--scroll',
    auto: 'box-overflow--auto'
  }),
  position: mapping({
    absolute: 'box-position-absolute',
    relative: 'box-position-relative',
    fixed: 'box-position-fixed',
    // default: static
  }),
  right: toggle('box-right0'),
  top: toggle('box-top0'),
  width: width => fromInlineStyle({ width }),
  wrap: toggle('box-flex-wrap'),
  shadow: toggle('box--shadow'),
  shape: mapping({
    'round': 'box-border-radius--round',
    'rounder': 'box-border-radius--rounder',
    'circle': 'box-border-radius--circle',
    // default: square
  }),
  border: mapping({
    'white': 'box-border-color--white',
    'darkGray': 'box-border-color--darkGray',
    'lightGray': 'box-border-color--lightGray',
    'midGray': 'box-border-color--midGray',
    'coolGray': 'box-border-color--coolGray',
    ...subjectBorderColorMap
  }),
  borderWidth: borderWidth => fromInlineStyle({borderWidth}),
  dangerouslySetInlineStyle: value =>
    value && value.__style ? fromInlineStyle(value.__style) : identity(),
}
const contains = (key, arr) => arr.indexOf(key) >= 0
const omit = (keys, obj) =>
  Object.keys(obj).reduce((acc, k) => {
    if (contains(k, keys)) {
      return acc
    }
    return {
      ...acc,
      [k]: obj[k],
    }
  }, {})

/*
# Component
*/
class Box extends React.PureComponent {
  render() {
    let blacklist = ['onClick', 'className', 'style']
    let s = fromClassName('box')
    const {children, ...props} = this.props
    for (const prop in props) {
      if (Object.prototype.hasOwnProperty.call(propToFn, prop)) {
        const fn = propToFn[prop]
        const value = props[prop]
        blacklist = blacklist.concat(prop)
        s = concat([s, fn(value)])
      }
    }
    return (
      <div {...omit(blacklist, props)} {...toProps(s)}>
        {children}
      </div>
    )
  }
}

export default Box


/*
# PropTypes
*/

const ColumnPropType = PropTypes.oneOf([
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
])

const MarginPropType = PropTypes.oneOf([
  -12,
  -11,
  -10,
  -9,
  -8,
  -7,
  -6,
  -5,
  -4,
  -3,
  -2,
  -1,
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
])

const PaddingPropType = PropTypes.oneOf([
  0,
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
])
Box.propTypes = {
  children: PropTypes.node,
  dangerouslySetInlineStyle: PropTypes.shape({
    __style: PropTypes.object,
  }),
  display: PropTypes.oneOf(['none', 'flex', 'block', 'inlineBlock']),
  direction: PropTypes.oneOf(['row', 'column']),
  column: ColumnPropType,

  smDisplay: PropTypes.oneOf(['none', 'flex', 'block', 'inlineBlock']),
  smDirection: PropTypes.oneOf(['row', 'column']),
  smColumn: ColumnPropType,

  mdDisplay: PropTypes.oneOf(['none', 'flex', 'block', 'inlineBlock']),
  mdDirection: PropTypes.oneOf(['row', 'column']),
  mdColumn: ColumnPropType,

  lgDisplay: PropTypes.oneOf(['none', 'flex', 'block', 'inlineBlock']),
  lgDirection: PropTypes.oneOf(['row', 'column']),
  lgColumn: ColumnPropType,

  alignContent: PropTypes.oneOf([
    'start',
    'end',
    'center',
    'between',
    'around',
    'stretch',
  ]),
  alignItems: PropTypes.oneOf([
    'start',
    'end',
    'center',
    'baseline',
    'stretch',
  ]),
  alignSelf: PropTypes.oneOf([
    'auto',
    'start',
    'end',
    'center',
    'baseline',
    'stretch',
  ]),
  bottom: PropTypes.bool,
  color: PropTypes.oneOf([
    'white',
    'darkGray',
    'midGray',
    'lightGray',
    'coolGray',
    ...Object.values(SubjectToCSSName).map(color=>`${color}-nav`),
    ...Object.values(SubjectToCSSName).map(color=>`${color}-search`),
    ...Object.values(SubjectToCSSName).map(color=>`${color}-subject`),
    ...Object.values(SubjectToCSSName).map(color=>`${color}-cta`),
  ]),
  flexGrow: PropTypes.number,
  flexShrink: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  justifyContent: PropTypes.oneOf([
    'start',
    'end',
    'center',
    'between',
    'around',
  ]),
  left: PropTypes.bool,

  margin: MarginPropType,
  marginTop: MarginPropType,
  marginRight: MarginPropType,
  marginBottom: MarginPropType,
  marginLeft: MarginPropType,

  smMargin: MarginPropType,
  smMarginTop: MarginPropType,
  smMarginRight: MarginPropType,
  smMarginBottom: MarginPropType,
  smMarginLeft: MarginPropType,

  mdMargin: MarginPropType,
  mdMarginTop: MarginPropType,
  mdMarginRight: MarginPropType,
  mdMarginBottom: MarginPropType,
  mdMarginLeft: MarginPropType,

  lgMargin: MarginPropType,
  lgMarginTop: MarginPropType,
  lgMarginRight: MarginPropType,
  lgMarginBottom: MarginPropType,
  lgMarginLeft: MarginPropType,

  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  overflow: PropTypes.oneOf([
    'visible',
    'hidden',
    'scroll',
    'scrollX',
    'scrollY',
    'auto',
  ]),

  padding: PaddingPropType,
  paddingX: PaddingPropType,
  paddingY: PaddingPropType,
  paddingTop: PaddingPropType,
  paddingRight: PaddingPropType,
  paddingBottom: PaddingPropType,
  paddingLeft: PaddingPropType,

  smPadding: PaddingPropType,
  smPaddingX: PaddingPropType,
  smPaddingY: PaddingPropType,
  smPaddingTop: PaddingPropType,
  smPaddingRight: PaddingPropType,
  smPaddingBottom: PaddingPropType,
  smPaddingLeft: PaddingPropType,

  mdPadding: PaddingPropType,
  mdPaddingX: PaddingPropType,
  mdPaddingY: PaddingPropType,
  mdPaddingTop: PaddingPropType,
  mdPaddingRight: PaddingPropType,
  mdPaddingBottom: PaddingPropType,
  mdPaddingLeft: PaddingPropType,

  lgPadding: PaddingPropType,
  lgPaddingX: PaddingPropType,
  lgPaddingY: PaddingPropType,
  lgPaddingTop: PaddingPropType,
  lgPaddingRight: PaddingPropType,
  lgPaddingBottom: PaddingPropType,
  lgPaddingLeft: PaddingPropType,

  position: PropTypes.oneOf(['static', 'absolute', 'relative', 'fixed']),
  right: PropTypes.bool,
  top: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  wrap: PropTypes.bool,
  shadow: PropTypes.bool,
  shape: PropTypes.oneOf(['round','rounder','circle', 'square']),
  border: PropTypes.oneOf([
    'darkGray',
    'lightGray',
    'coolGray',
    'midGray',
    'white',
    ...Object.values(SubjectToCSSName).map(color=>`${color}-nav`),
    ...Object.values(SubjectToCSSName).map(color=>`${color}-search`),
    ...Object.values(SubjectToCSSName).map(color=>`${color}-subject`),
    ...Object.values(SubjectToCSSName).map(color=>`${color}-cta`),
  ]),
  borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}



// WEBPACK FOOTER //
// ./src/core/components/box/Box.js